import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  message,
  Modal,
  Input,
  Typography,
  Row,
  Col,
  Statistic,
  Badge,
  Descriptions,
  Alert
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ShopOutlined,
  GiftOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import adminPartnerService from '../../services/adminPartnerService';
import BackButton from '../../components/BackButton';

const { Title, Text } = Typography;
const { TextArea } = Input;

const BundleRequestList = observer(() => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionType, setActionType] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const pendingResult = await adminPartnerService.getPendingBundleRequests();
      setRequests(pendingResult.requests || []);
      setPendingCount(pendingResult.count || 0);
    } catch (error) {
      message.error(t('admin.bundles.loadFailed'));
    }
    setLoading(false);
  };

  const openActionModal = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setAdminNotes('');
    setModalVisible(true);
  };

  const handleAction = async () => {
    if (!selectedRequest || !actionType) return;

    setProcessing(true);
    try {
      if (actionType === 'approve') {
        await adminPartnerService.approveBundleRequest(selectedRequest.id, adminNotes);
        message.success(t('admin.bundles.approveSuccess'));
      } else {
        await adminPartnerService.rejectBundleRequest(selectedRequest.id, adminNotes);
        message.success(t('admin.bundles.rejectSuccess'));
      }
      setModalVisible(false);
      loadData();
    } catch (error) {
      message.error(error.response?.data?.error || t('admin.bundles.actionFailed'));
    }
    setProcessing(false);
  };

  const columns = [
    {
      title: t('admin.bundles.columns.partner'),
      key: 'partner',
      width: 180,
      ellipsis: true,
      render: (_, record) => (
        <Space>
          <ShopOutlined />
          <Link to={`/admin/partners/${record.partner?.id}`}>
            {record.partner?.store_name || record.partner?.user_email || '-'}
          </Link>
        </Space>
      )
    },
    {
      title: t('admin.bundles.columns.bundle'),
      dataIndex: 'package_type_display',
      key: 'package_type_display',
      width: 150,
      render: (text) => (
        <Tag color="blue" icon={<GiftOutlined />}>
          {text}
        </Tag>
      )
    },
    {
      title: t('admin.bundles.columns.extraItems'),
      dataIndex: 'extra_items',
      key: 'extra_items',
      width: 80,
      align: 'center',
      render: (items) => <Badge count={`+${items}`} style={{ backgroundColor: '#52c41a' }} />
    },
    {
      title: t('admin.bundles.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => {
        const config = {
          PENDING_APPROVAL: { color: 'orange', icon: <ClockCircleOutlined />, text: t('admin.bundles.status.pending') },
          APPROVED: { color: 'green', icon: <CheckCircleOutlined />, text: t('admin.bundles.status.approved') },
          REJECTED: { color: 'red', icon: <CloseCircleOutlined />, text: t('admin.bundles.status.rejected') }
        };
        const { color, icon, text } = config[status] || { color: 'default', text: status };
        return <Tag color={color} icon={icon}>{text}</Tag>;
      }
    },
    {
      title: t('admin.bundles.columns.requestNotes'),
      dataIndex: 'request_notes',
      key: 'request_notes',
      width: 180,
      ellipsis: true,
      render: (notes) => notes || <Text type="secondary">-</Text>
    },
    {
      title: t('admin.bundles.columns.requestedAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => new Date(date).toLocaleString()
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 180,
      fixed: 'right',
      align: 'center',
      render: (_, record) => {
        if (record.status !== 'PENDING_APPROVAL') {
          return (
            <Tag color={record.status === 'APPROVED' ? 'green' : 'red'}>
              {record.status === 'APPROVED' ? t('admin.bundles.status.approved') : t('admin.bundles.status.rejected')}
            </Tag>
          );
        }
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => openActionModal(record, 'approve')}
            >
              {t('admin.bundles.approve')}
            </Button>
            <Button
              danger
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={() => openActionModal(record, 'reject')}
            >
              {t('admin.bundles.reject')}
            </Button>
          </Space>
        );
      }
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <BackButton to="/admin/dashboard" label={t('admin.bundles.backToDashboard')} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>
            <GiftOutlined /> {t('admin.bundles.title')}
          </Title>
          <Text type="secondary">{t('admin.bundles.description')}</Text>
        </div>
        <Badge count={pendingCount} offset={[10, 0]}>
          <Tag color="orange" style={{ fontSize: 14, padding: '4px 12px' }}>
            <ClockCircleOutlined /> {t('admin.bundles.pendingRequests', { count: pendingCount })}
          </Tag>
        </Badge>
      </div>

      <Card>
        <Table
          dataSource={requests}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1050 }}
          locale={{
            emptyText: t('admin.bundles.noRequests')
          }}
        />
      </Card>

      {/* Action Modal */}
      <Modal
        title={actionType === 'approve' ? t('admin.bundles.approveTitle') : t('admin.bundles.rejectTitle')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleAction}
        confirmLoading={processing}
        okText={actionType === 'approve' ? t('admin.bundles.confirmApprove') : t('admin.bundles.confirmReject')}
        okButtonProps={{ danger: actionType === 'reject' }}
        cancelText={t('common.cancel')}
      >
        {selectedRequest && (
          <>
            <Descriptions column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('admin.bundles.columns.partner')}>
                {selectedRequest.partner?.store_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.bundles.columns.bundle')}>
                <Tag color="blue">{selectedRequest.package_type_display}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.bundles.columns.extraItems')}>
                +{selectedRequest.extra_items} {t('admin.bundles.items')}
              </Descriptions.Item>
              {selectedRequest.request_notes && (
                <Descriptions.Item label={t('admin.bundles.columns.requestNotes')}>
                  {selectedRequest.request_notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            {actionType === 'approve' && (
              <Alert
                message={t('admin.bundles.approveWarning')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {actionType === 'reject' && (
              <Alert
                message={t('admin.bundles.rejectWarning')}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <div style={{ marginBottom: 8 }}>
              <Text strong>{t('admin.bundles.adminNotes')}</Text>
              <Text type="secondary" style={{ marginLeft: 4 }}>
                ({t('common.optional')})
              </Text>
            </div>
            <TextArea
              rows={3}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder={t('admin.bundles.adminNotesPlaceholder')}
            />
          </>
        )}
      </Modal>
    </div>
  );
});

export default BundleRequestList;
